/**
 * Advanced Data Table Export Utilities
 * Export functionality for CSV, Excel, and PDF formats
 * Part of CR-005 UI/UX Modernization - Unit 3: Advanced Table Component
 */

import * as Papa from 'papaparse';
import * as ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import type { DataTableColumn, ExportOptions, TableData } from '../types';

/**
 * Default date format for exports
 */
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/**
 * Format a value for export based on its type
 */
function formatExportValue<TData extends TableData>(
  value: unknown,
  column: DataTableColumn<TData>,
  options?: Partial<ExportOptions>
): string {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return '';
  }

  // Use custom formatter if provided
  if (column.exportFormatter) {
    return column.exportFormatter(value, {} as TData);
  }

  // Handle dates
  if (value instanceof Date) {
    try {
      return format(value, options?.dateFormat || DEFAULT_DATE_FORMAT);
    } catch {
      return String(value);
    }
  }

  // Handle Date strings (ISO format)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return format(date, options?.dateFormat || DEFAULT_DATE_FORMAT);
      }
    } catch {
      // Fall through to string handling
    }
  }

  // Handle numbers
  if (typeof value === 'number') {
    const numberFormat = options?.numberFormat;
    if (numberFormat) {
      const decimals = numberFormat.decimals ?? 2;
      const formatted = value.toFixed(decimals);

      if (numberFormat.thousandsSeparator || numberFormat.decimalSeparator) {
        const [whole, decimal] = formatted.split('.');
        const wholeFormatted = numberFormat.thousandsSeparator
          ? whole!.replace(/\B(?=(\d{3})+(?!\d))/g, numberFormat.thousandsSeparator)
          : whole!;

        return decimal
          ? `${wholeFormatted}${numberFormat.decimalSeparator || '.'}${decimal}`
          : wholeFormatted;
      }

      return formatted;
    }
    return String(value);
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Handle objects
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[Object]';
    }
  }

  // Default: convert to string
  return String(value);
}

/**
 * Get column header name
 */
function getColumnHeader<TData extends TableData>(
  column: DataTableColumn<TData>,
  options?: Partial<ExportOptions>
): string {
  // Check custom headers first
  const columnId = 'id' in column ? (column as { id?: string }).id : undefined;
  if (options?.customHeaders && columnId && options.customHeaders[columnId]) {
    return options.customHeaders[columnId];
  }

  // Use column header
  const header = 'header' in column ? (column as { header?: unknown }).header : undefined;
  if (typeof header === 'string') {
    return header;
  }

  // Use column id or accessor key
  if (columnId) {
    return columnId;
  }

  const accessorKey = 'accessorKey' in column ? (column as { accessorKey?: unknown }).accessorKey : undefined;
  if (accessorKey && typeof accessorKey === 'string') {
    return accessorKey;
  }

  return 'Column';
}

/**
 * Get cell value from row data
 */
function getCellValue<TData extends TableData>(row: TData, column: DataTableColumn<TData>): unknown {
  // Use accessor function if provided
  const accessorFn = 'accessorFn' in column ? (column as { accessorFn?: (row: TData, index: number) => unknown }).accessorFn : undefined;
  if (accessorFn) {
    return accessorFn(row, 0);
  }

  // Use accessor key
  const accessorKey = 'accessorKey' in column ? (column as { accessorKey?: unknown }).accessorKey : undefined;
  if (accessorKey) {
    const key = accessorKey as string;
    return (row as Record<string, unknown>)[key];
  }

  // Use column id
  const columnId = 'id' in column ? (column as { id?: string }).id : undefined;
  if (columnId) {
    return (row as Record<string, unknown>)[columnId];
  }

  return null;
}

/**
 * Filter exportable columns
 */
function getExportableColumns<TData extends TableData>(
  columns: DataTableColumn<TData>[]
): DataTableColumn<TData>[] {
  return columns.filter((column) => {
    // Skip non-exportable columns
    if (column.exportable === false) {
      return false;
    }

    // Skip columns without data access
    const hasAccessorKey = 'accessorKey' in column && (column as { accessorKey?: unknown }).accessorKey;
    const hasAccessorFn = 'accessorFn' in column && (column as { accessorFn?: unknown }).accessorFn;
    const hasId = 'id' in column && (column as { id?: string }).id;

    if (!hasAccessorKey && !hasAccessorFn && !hasId) {
      return false;
    }

    return true;
  });
}

/**
 * Export data to CSV format
 *
 * @param data - Array of data objects to export
 * @param columns - Column definitions
 * @param filename - Base filename (without extension)
 * @param options - Export options
 *
 * @example
 * ```typescript
 * const data = [{ id: 1, name: 'John', age: 30 }];
 * const columns = [
 *   { accessorKey: 'id', header: 'ID' },
 *   { accessorKey: 'name', header: 'Name' },
 *   { accessorKey: 'age', header: 'Age' }
 * ];
 *
 * exportToCSV(data, columns, 'users');
 * ```
 */
export function exportToCSV<TData extends TableData>(
  data: TData[],
  columns: DataTableColumn<TData>[],
  filename: string,
  options?: Partial<Omit<ExportOptions, 'format' | 'filename'>>
): void {
  try {
    // Validate inputs
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    if (!columns || columns.length === 0) {
      throw new Error('No columns defined');
    }

    // Get exportable columns
    const exportColumns = getExportableColumns(columns);

    if (exportColumns.length === 0) {
      throw new Error('No exportable columns found');
    }

    // Build CSV data
    const csvData = data.map((row) => {
      const csvRow: Record<string, string> = {};

      exportColumns.forEach((column) => {
        const header = getColumnHeader(column, options);
        const value = getCellValue(row, column);
        csvRow[header] = formatExportValue(value, column, options);
      });

      return csvRow;
    });

    // Generate CSV
    const csv = Papa.unparse(csvData, {
      header: true,
      skipEmptyLines: true,
    });

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error(`Failed to export to CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Export data to Excel format
 *
 * @param data - Array of data objects to export
 * @param columns - Column definitions
 * @param filename - Base filename (without extension)
 * @param options - Export options
 *
 * @example
 * ```typescript
 * const data = [{ id: 1, name: 'John', age: 30 }];
 * const columns = [
 *   { accessorKey: 'id', header: 'ID' },
 *   { accessorKey: 'name', header: 'Name' },
 *   { accessorKey: 'age', header: 'Age' }
 * ];
 *
 * exportToExcel(data, columns, 'users', {
 *   excelOptions: { sheetName: 'Users', freezeHeader: true }
 * });
 * ```
 */
export async function exportToExcel<TData extends TableData>(
  data: TData[],
  columns: DataTableColumn<TData>[],
  filename: string,
  options?: Partial<Omit<ExportOptions, 'format' | 'filename'>>
): Promise<void> {
  try {
    // Validate inputs
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    if (!columns || columns.length === 0) {
      throw new Error('No columns defined');
    }

    // Get exportable columns
    const exportColumns = getExportableColumns(columns);

    if (exportColumns.length === 0) {
      throw new Error('No exportable columns found');
    }

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const sheetName = options?.excelOptions?.sheetName || 'Sheet1';
    const worksheet = workbook.addWorksheet(sheetName);

    // Set up columns
    worksheet.columns = exportColumns.map((column) => {
      const columnId = 'id' in column ? (column as { id?: string }).id : undefined;
      const accessorKey = 'accessorKey' in column ? (column as { accessorKey?: unknown }).accessorKey : undefined;
      return {
        header: getColumnHeader(column, options),
        key: columnId || accessorKey?.toString() || 'column',
        ...(options?.excelOptions?.autoFitColumns ? {} : { width: 15 }),
      };
    });

    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE5E7EB' }, // neutral-200 from design tokens
    };
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'left' };

    // Add data rows
    data.forEach((row) => {
      const excelRow: Record<string, string> = {};

      exportColumns.forEach((column) => {
        const columnId = 'id' in column ? (column as { id?: string }).id : undefined;
        const accessorKey = 'accessorKey' in column ? (column as { accessorKey?: unknown }).accessorKey : undefined;
        const key = columnId || accessorKey?.toString() || 'column';
        const value = getCellValue(row, column);
        excelRow[key] = formatExportValue(value, column, options);
      });

      worksheet.addRow(excelRow);
    });

    // Auto-fit columns if requested
    if (options?.excelOptions?.autoFitColumns) {
      worksheet.columns.forEach((column) => {
        if (!column.eachCell) return;

        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value?.toString() || '';
          maxLength = Math.max(maxLength, cellValue.length);
        });

        column.width = Math.min(Math.max(maxLength + 2, 10), 50);
      });
    }

    // Freeze header row if requested
    if (options?.excelOptions?.freezeHeader) {
      worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    }

    // Add borders to all cells
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD1D5DB' } }, // neutral-300
          left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        };
      });
    });

    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.xlsx`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error(`Failed to export to Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Export data to PDF format
 *
 * @param data - Array of data objects to export
 * @param columns - Column definitions
 * @param filename - Base filename (without extension)
 * @param options - Export options
 *
 * @example
 * ```typescript
 * const data = [{ id: 1, name: 'John', age: 30 }];
 * const columns = [
 *   { accessorKey: 'id', header: 'ID' },
 *   { accessorKey: 'name', header: 'Name' },
 *   { accessorKey: 'age', header: 'Age' }
 * ];
 *
 * exportToPDF(data, columns, 'users', {
 *   pdfOptions: {
 *     orientation: 'landscape',
 *     title: 'User List',
 *     includePageNumbers: true
 *   }
 * });
 * ```
 */
export function exportToPDF<TData extends TableData>(
  data: TData[],
  columns: DataTableColumn<TData>[],
  filename: string,
  options?: Partial<Omit<ExportOptions, 'format' | 'filename'>>
): void {
  try {
    // Validate inputs
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    if (!columns || columns.length === 0) {
      throw new Error('No columns defined');
    }

    // Get exportable columns
    const exportColumns = getExportableColumns(columns);

    if (exportColumns.length === 0) {
      throw new Error('No exportable columns found');
    }

    // PDF options
    const orientation = options?.pdfOptions?.orientation || 'portrait';
    const pageSize = options?.pdfOptions?.pageSize || 'a4';

    // Create PDF document
    const doc = new jsPDF({
      orientation,
      unit: 'mm',
      format: pageSize,
    });

    // Add title if provided
    if (options?.pdfOptions?.title) {
      doc.setFontSize(16);
      doc.setFont('', 'bold');
      doc.text(options.pdfOptions.title, 14, 15);
    }

    // Prepare table headers
    const headers = exportColumns.map((column) => getColumnHeader(column, options));

    // Prepare table body
    const body = data.map((row) =>
      exportColumns.map((column) => {
        const value = getCellValue(row, column);
        return formatExportValue(value, column, options);
      })
    );

    // Generate table
    autoTable(doc, {
      head: [headers],
      body,
      startY: options?.pdfOptions?.title ? 25 : 15,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [209, 213, 219], // neutral-300 from design tokens
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [229, 231, 235], // neutral-200 from design tokens
        textColor: [17, 24, 39], // neutral-900
        fontStyle: 'bold',
        halign: 'left',
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251], // neutral-50 from design tokens
      },
      margin: { top: 10, right: 10, bottom: 10, left: 10 },
      didDrawPage: (data) => {
        // Add page numbers if requested
        if (options?.pdfOptions?.includePageNumbers) {
          const pageCount = doc.getNumberOfPages();
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.getHeight();

          doc.setFontSize(8);
          doc.setFont('', 'normal');
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageSize.getWidth() / 2,
            pageHeight - 5,
            { align: 'center' }
          );
        }

        // Add export date in footer
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.getHeight();
        doc.setFontSize(7);
        doc.setTextColor(107, 114, 128); // neutral-500
        doc.text(
          `Exported on ${format(new Date(), 'yyyy-MM-dd HH:mm')}`,
          14,
          pageHeight - 5
        );
      },
    });

    // Add metadata
    if (options?.pdfOptions?.title) {
      doc.setProperties({
        title: options.pdfOptions.title,
        author: options.pdfOptions.author || 'InspectaOne',
        subject: 'Data Export',
        creator: 'InspectaOne Data Table',
      });
    }

    // Save PDF
    doc.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error(`Failed to export to PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Generic export function that delegates to format-specific exporters
 *
 * @param data - Array of data objects to export
 * @param columns - Column definitions
 * @param options - Export options including format and filename
 *
 * @example
 * ```typescript
 * const data = [{ id: 1, name: 'John' }];
 * const columns = [{ accessorKey: 'id', header: 'ID' }];
 *
 * // Export to CSV
 * await exportData(data, columns, { format: 'csv', filename: 'users' });
 *
 * // Export to Excel
 * await exportData(data, columns, { format: 'excel', filename: 'users' });
 *
 * // Export to PDF
 * await exportData(data, columns, { format: 'pdf', filename: 'users' });
 * ```
 */
export async function exportData<TData extends TableData>(
  data: TData[],
  columns: DataTableColumn<TData>[],
  options: ExportOptions
): Promise<void> {
  const { format, filename, ...restOptions } = options;

  switch (format) {
    case 'csv':
      exportToCSV(data, columns, filename, restOptions);
      break;
    case 'excel':
      await exportToExcel(data, columns, filename, restOptions);
      break;
    case 'pdf':
      exportToPDF(data, columns, filename, restOptions);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}
